import { Outlet, useLocation, useNavigate } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { DotIndicator } from '@/components/onboarding';
import { useOnboardingStore } from '@/stores/onboardingStore';

const STEPS = [
  '/onboarding/welcome',
  '/onboarding/schedule-style',
  '/onboarding/energy-pattern',
  '/onboarding/connect',
  '/onboarding/first-routine',
] as const;

const SKIPPABLE_FROM = 3;

export function OnboardingLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const completeOnboarding = useOnboardingStore((s) => s.completeOnboarding);
  const currentIndex = STEPS.indexOf(pathname as (typeof STEPS)[number]);
  const step = currentIndex === -1 ? 0 : currentIndex;
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;
  const isSkippable = step >= SKIPPABLE_FROM;

  const goBack = () => {
    if (!isFirst) void navigate(STEPS[step - 1]);
  };

  const goNext = () => {
    if (!isLast) void navigate(STEPS[step + 1]);
  };

  return (
    <div className="flex min-h-[100dvh] flex-col bg-canvas px-6 pt-[env(safe-area-inset-top)] pb-[calc(env(safe-area-inset-bottom)+12px)] md:items-center md:justify-center md:py-12">
      {/* Step panel — plain full-height column on mobile; a centered paper
          panel (hairline border, no shadow) on md+. */}
      <div className="flex w-full flex-1 flex-col md:max-w-xl md:flex-none md:min-h-[560px] md:rounded-2xl md:border md:border-line-2 md:bg-card md:p-8 lg:p-10">
        {/* Panel header line: back + dots + spacer (dots carry the step
            count for screen readers — no visible "Step N of M" line). */}
        {step > 0 && (
          <div className="flex h-12 items-center justify-between gap-3 md:h-auto">
            <button
              type="button"
              onClick={goBack}
              disabled={isFirst}
              aria-label="Back"
              className="inline-flex h-7 w-7 items-center justify-center rounded-[7px] text-t2 transition-colors can-hover:hover:text-t1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas md:focus-visible:ring-offset-card disabled:pointer-events-none disabled:opacity-0 pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]"
            >
              <ChevronLeft className="h-[18px] w-[18px]" />
            </button>
            <DotIndicator total={STEPS.length} current={step} />
            <div className="w-7 pointer-coarse:w-11" />
          </div>
        )}

        {/* Page content */}
        <div className="flex flex-1 flex-col pt-6 md:pt-8">
          <Outlet context={{ goNext, goBack, isLast }} />
        </div>
      </div>

      {/* Skip link — pinned under the content on mobile, centered under the
          panel on md+. */}
      {isSkippable && (
        <div className="flex flex-col items-center gap-3 pb-6 md:pb-0 md:pt-4">
          <button
            type="button"
            onClick={
              isLast
                ? () => {
                    completeOnboarding();
                    void navigate('/app');
                  }
                : goNext
            }
            className="inline-flex items-center justify-center rounded-lg px-4 text-body text-t2 transition-colors can-hover:hover:text-t1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas pointer-coarse:min-h-[44px]"
          >
            Skip
          </button>
        </div>
      )}
    </div>
  );
}
